// Pattern: docs/design-patterns/anti-patterns/premature-generalization.md (ship only the chrome fields PRD 1 needs)
//
// Per-route page chrome (title/subtitle/breadcrumbs/controls): a screen opts
// in via useSetPageChrome, which sets on mount and clears on unmount. A
// later-mounted nested route's chrome (e.g. a detail view rendered into a
// parent layout route's <Outlet/>) takes over from an already-mounted
// parent's, since only the new component's effect fires. When the deepest
// setter unmounts, the reader falls back straight to the route-derived
// default — it does NOT restore the parent's chrome (no restore-parent
// bookkeeping in PRD 1; see spec B6.2). usePageChrome() never returns null
// for the same reason: nothing set = a route-derived default, so every
// route shows a sane title before/without per-page adoption (this PRD ships
// the mechanism only; per-page adoption is a follow-up — see spec Product
// Decision #3).
//
// Deliberately independent of `navConfig`/`components/shell` (contexts/ sits
// below components/ in this app's layering — see e.g. AuthContext.tsx,
// ThemeContext.tsx). TopBar, which *does* know about navConfig, passes a
// richer fallback in via usePageChrome's optional `fallbackTitle` argument.
import type { JSX, ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type SetPageChrome = (chrome: PageChrome | null) => void;

export interface PageChrome {
  title: string;
  subtitle?: string;
  breadcrumbs?: Array<{ label: string; to?: string }>;
  controls?: ReactNode;
}

const ChromeContext = createContext<PageChrome | null>(null);
const SetChromeContext = createContext<SetPageChrome | null>(null);

export interface PageChromeProviderProps {
  children: ReactNode;
}

/**
 * Humanizes the last non-empty path segment as a title fallback, e.g.
 * `/settings/profile` -> "Profile", `/org` -> "Org".
 */
export function humanizeLastPathSegment(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];

  if (!lastSegment) {
    return 'Compounds';
  }

  return lastSegment
    .split(/[-_]/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function PageChromeProvider({ children }: PageChromeProviderProps): JSX.Element {
  const [chrome, setChrome] = useState<PageChrome | null>(null);

  return (
    <SetChromeContext.Provider value={setChrome}>
      <ChromeContext.Provider value={chrome}>{children}</ChromeContext.Provider>
    </SetChromeContext.Provider>
  );
}

/**
 * Reads the page chrome only if a screen has explicitly adopted it via
 * useSetPageChrome — unlike usePageChrome(), never substitutes a
 * route-derived default. For a consumer (e.g. the legacy Header) that must
 * render nothing rather than a redundant heading on a screen that hasn't
 * adopted PageChrome yet and still renders its own local <h1>.
 */
export function useOptionalPageChrome(): PageChrome | null {
  return useContext(ChromeContext);
}

/**
 * Reads the active page chrome: the chrome set by the deepest mounted
 * useSetPageChrome caller, or — when none is set — a route-derived default
 * so the title is never blank. `fallbackTitle` lets a caller (TopBar, via
 * navConfig) supply a better default than the humanized path segment;
 * omit it to get the plain route-derived default.
 */
export function usePageChrome(fallbackTitle?: string): PageChrome {
  const chrome = useContext(ChromeContext);
  const { pathname } = useLocation();

  if (chrome) {
    return chrome;
  }

  return { title: fallbackTitle ?? humanizeLastPathSegment(pathname) };
}

/**
 * Sets the page chrome for as long as the calling component stays mounted;
 * clears it on unmount. `chrome` MUST be a stable reference (useMemo) —
 * a new object identity every render re-fires this effect every render.
 */
export function useSetPageChrome(chrome: PageChrome): void {
  const setChrome = useContext(SetChromeContext);

  useEffect(() => {
    if (!setChrome) {
      return undefined;
    }

    setChrome(chrome);
    return (): void => setChrome(null);
  }, [chrome, setChrome]);
}
